import { makeObservable, runInAction, action, observable, computed } from "mobx";
import { WithAppStore } from "@/store/types";
import { AppStore } from "@/store";
import { CacheKey } from "@/modules/cache/constants";
import { BaseError } from "@/domains/errors";
import { objectModule } from "@/modules/object";
import { logger } from "@/modules/logger";
import { asyncResultModule } from "@/modules/async-result";
import { AsyncResult } from "@/modules/async-result/types";
import { clientEnvModule } from "@/modules/client-env";

type PerDeviceOnboardingInfoData = {
  hasDismissedMeetingOnboardingSystemPermissions: boolean;
  hasDismissedVoiceOnboardingSystemPermissions: boolean;
};

const DEFAULT_PER_DEVICE_ONBOARDING_INFO_DATA: PerDeviceOnboardingInfoData = {
  hasDismissedMeetingOnboardingSystemPermissions: false,
  hasDismissedVoiceOnboardingSystemPermissions: false,
};

export class PerDeviceOnboardingInfoObservable {
  store: AppStore;
  private deviceOnboardingInfo: AsyncResult<PerDeviceOnboardingInfoData> =
    asyncResultModule.setLoading();

  constructor({ store }: WithAppStore) {
    this.store = store;

    makeObservable<
      PerDeviceOnboardingInfoObservable,
      | "deviceOnboardingInfo"
      | "currentOnboardingSystemPermissionsData"
      | "shouldShowMeetingOnboardingSystemPermissions"
      | "shouldShowVoiceOnboardingSystemPermissions"
      | "_resolveOnboardingInfoDataBasedOnDeviceState"
      | "_hydrateOnboardingInfo"
    >(this, {
      store: false,
      deviceOnboardingInfo: observable,
      shouldShowMeetingOnboardingSystemPermissions: computed,
      shouldShowVoiceOnboardingSystemPermissions: computed,
      currentOnboardingSystemPermissionsData: computed,
      _resolveOnboardingInfoDataBasedOnDeviceState: action,
      _hydrateOnboardingInfo: false,
      initialize: action,
      resetState: action,
      dismissMeetingOnboardingSystemPermissions: action,
      dismissVoiceOnboardingSystemPermissions: action,
    });
  }

  /**
   * Note that we use the `inverse` here instead of hasDismissedOnboardingSystemPermissions`.
   *
   * This is because:
   * - If we hit a snag when hydrating, we want to default to -not- showing the onboarding steps.
   */
  get shouldShowMeetingOnboardingSystemPermissions(): boolean {
    // Meeting detection depends on recall SDK, which is not supported on Intel-based MacOS devices.
    if (this.store.electron.isIntelMacDesktop) return false;

    if (!this.deviceOnboardingInfo.data) return false;
    if (!this.deviceOnboardingInfo.data.hasDismissedMeetingOnboardingSystemPermissions) {
      return true;
    }

    const enabled = this.store.systemPermissions.meetingNotificationEnabled;
    if (!enabled) return false;

    // Do we have the required permissions?
    return !this.store.systemPermissions.hasRequiredMeetingPermissions;
  }

  get shouldShowVoiceOnboardingSystemPermissions(): boolean {
    /**
     * We only support this feature on MacOS.
     * But, we don't support it on Intel-based MacOS devices.
     */
    if (!this.store.interface.isMacOs) return false;
    if (this.store.electron.isIntelMacDesktop) return false;

    if (!this.deviceOnboardingInfo.data) return false;
    return !this.deviceOnboardingInfo.data.hasDismissedVoiceOnboardingSystemPermissions;
  }

  private get currentOnboardingSystemPermissionsData(): PerDeviceOnboardingInfoData {
    return this.deviceOnboardingInfo.data ?? DEFAULT_PER_DEVICE_ONBOARDING_INFO_DATA;
  }

  dismissMeetingOnboardingSystemPermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedMeetingOnboardingSystemPermissions: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  dismissVoiceOnboardingSystemPermissions() {
    runInAction(() => {
      const currentData = this.currentOnboardingSystemPermissionsData;

      const nextData: PerDeviceOnboardingInfoData = {
        ...currentData,
        hasDismissedVoiceOnboardingSystemPermissions: true,
      };

      this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, nextData);
      this.deviceOnboardingInfo = asyncResultModule.setReady(nextData);
    });
  }

  /**
   * This determines the "initial" state for users.
   */
  private async _resolveOnboardingInfoDataBasedOnDeviceState(): Promise<PerDeviceOnboardingInfoData> {
    // await this.store.systemPermissions.syncPermissions();

    // const isMeetingNotificationEnabledResult = await electronModule.getMeetingNotificationEnabled();

    // const alreadyHasMeetingPermissions = this.store.systemPermissions.hasRequiredMeetingPermissions;
    // const alreadyHasVoicePermissions = this.store.systemPermissions.hasRequiredVoicePermissions;

    // /**
    //  * If the user has enabled the notification setting AND they have the required permissions, then we can
    //  * skip this screen.
    //  *
    //  * For all other users, we show this screen.
    //  */
    // const hasDismissedMeetingOnboardingSystemPermissions = (() => {
    //   if (alreadyHasMeetingPermissions && isMeetingNotificationEnabledResult.enabled) {
    //     return true;
    //   }

    //   return false;
    // })();

    // /**
    //  * If the user has the required voice permissions, then we can skip this screen.
    //  * (Most existing users will just skip this one.)
    //  */
    // const hasDismissedVoiceOnboardingSystemPermissions = !!alreadyHasVoicePermissions;

    // return {
    //   hasDismissedMeetingOnboardingSystemPermissions,
    //   hasDismissedVoiceOnboardingSystemPermissions,
    // };

    // Show the onboarding screen for all users.
    return {
      hasDismissedMeetingOnboardingSystemPermissions: false,
      hasDismissedVoiceOnboardingSystemPermissions: false,
    };
  }

  private async _hydrateOnboardingInfo() {
    try {
      if (!clientEnvModule.isDesktop()) return;

      // First, try to get data from accountCache (new location)
      let cachedData = await this.store.accountCache.get(CacheKey.PerDeviceOnboardingInfo);

      // Migration path: if accountCache is empty, check globalCache and migrate
      if (!cachedData) {
        const globalCacheData = await this.store.publicAppStore.globalCache.get(
          CacheKey.PerDeviceOnboardingInfo
        );

        if (globalCacheData) {
          // Migrate data from globalCache to accountCache
          this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, globalCacheData);
          cachedData = globalCacheData;

          logger.info({
            message:
              "[PerDeviceOnboardingInfoObservable] Migrated onboarding info from globalCache to accountCache",
          });
        }
      }

      if (!cachedData) {
        const resolvedData = await this._resolveOnboardingInfoDataBasedOnDeviceState();

        this.store.accountCache.set(CacheKey.PerDeviceOnboardingInfo, resolvedData);
        this.deviceOnboardingInfo = asyncResultModule.setReady(resolvedData);

        return;
      }

      runInAction(() => {
        this.deviceOnboardingInfo = asyncResultModule.setReady({
          hasDismissedMeetingOnboardingSystemPermissions:
            cachedData.hasDismissedMeetingOnboardingSystemPermissions,
          hasDismissedVoiceOnboardingSystemPermissions:
            cachedData.hasDismissedVoiceOnboardingSystemPermissions,
        });
      });
    } catch (unknownErr) {
      const err = unknownErr as BaseError;

      logger.warn({
        message: "[PerDeviceOnboardingInfoObservable] Failed to hydrate onboarding info",
        info: { error: objectModule.safeErrorAsJson(err) },
      });

      runInAction(() => {
        this.deviceOnboardingInfo = asyncResultModule.setError(err);
      });
    }
  }

  async initialize() {
    await this._hydrateOnboardingInfo();
  }

  resetState() {
    runInAction(() => {
      this.deviceOnboardingInfo = asyncResultModule.setInitial();
    });
  }
}
